import {
  Environment,
  WitboostCommercialModule,
} from '@agilelab/plugin-wb-platform-common';
import { createPermission } from '@mainstage/plugin-permission-common';
import { RbacPermission } from './model';

export const RESOURCE_TYPE_WITBOOST_ENTITY = 'witboost-entity';

export const catalogPlatformCreatePermission = {
  ...createPermission({
    name: 'catalog.platform.create',
    attributes: {},
  }),
  description:
    'Authorizes creating new entities. This enables any entity to be registered, and not only project ones. This permission also requires the catalog.entity.create permission to be enabled. Moreover, this permission enables the Template Studio link.',
  requiredModules: [],
};

export const catalogPlatformDeletePermission = {
  ...createPermission({
    name: 'catalog.platform.delete',
    attributes: {},
  }),
  description:
    'Authorizes removing entities (unregistration). This enables any entity to be unregistered, and not only project ones. This permission also requires the catalog.entity.delete permission to be enabled.',
  requiredModules: [],
};

export const catalogPlatformRefreshPermission = {
  ...createPermission({
    name: 'catalog.platform.refresh',
    attributes: {},
  }),
  description:
    'Authorizes refreshing entities. This enables any entity to be refreshed, and not only project ones..',
  requiredModules: [],
};

export const builderDpSnapshotCreatePermission = {
  ...createPermission({
    name: 'builder.dp.snapshot.create',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Authorizes the creation of a new release entity with version x.y.z-SNAPSHOT.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const builderDpReleasePermission = {
  ...createPermission({
    name: 'builder.dp.release',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description: "Authorizes the release of an entity's release.",
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const builderDpCommitPermission = {
  ...createPermission({
    name: 'builder.dp.commit',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Authorizes updating the release entity and increase the minor version.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const builderDpDeployPermission = (env: string) => {
  return {
    ...createPermission({
      name: `builder.dp.deploy.${env}`,
      attributes: {},
      resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
    }),
    description: `Authorizes deploy action on a system. You can specify a permission for each environment.`,
    requiredModules: [WitboostCommercialModule.ControlPlane],
  };
};

export const builderDpPoliciesTest = {
  ...createPermission({
    name: 'builder.dp.policies.test',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description: 'Authorizes testing policies on a system.',
  requiredModules: [
    WitboostCommercialModule.ControlPlane,
    WitboostCommercialModule.ComputationalGovernance,
  ],
};

export const builderDpNewVersion = {
  ...createPermission({
    name: 'builder.dp.newversion',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description: 'Authorizes cloning of a system with a major version increment.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const builderSoftwareCatalogView = {
  ...createPermission({
    name: 'builder.software-catalog.view',
    attributes: {},
  }),
  description: 'Authorizes the user to see the "Entities" page.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const cgpEntityEdit = {
  ...createPermission({
    name: 'cgp.entity.edit',
    attributes: {},
  }),
  description:
    'Authorizes the view/creation/editing/deletion/testing and change status of a policy/metric. Moreover, Flags column in marketplace is visible and Flag and Score dialog is accessible.',
  requiredModules: [WitboostCommercialModule.ComputationalGovernance],
};

export const cgpEntityView = {
  ...createPermission({
    name: 'cgp.entity.view',
    attributes: {},
  }),
  description:
    'Authorizes the view of a policy/metric. Moreover, Flags column in marketplace is visible and Flag and Score dialog is accessible.',
  requiredModules: [WitboostCommercialModule.ComputationalGovernance],
};

export const platformSettingsEditPermission = {
  ...createPermission({
    name: 'platform.settings.edit',
    attributes: {},
  }),
  description:
    'Authorizes the view/use of the platform settings page. Who can view the settings page can also edit them, there is no distinction yet.',
  requiredModules: [],
};

export const platformCustomViewEditPermission = {
  ...createPermission({
    name: 'platform.custom-view.edit',
    attributes: {},
  }),
  description:
    'Can see and use the download button for Custom Views, and use the REST API to add a new custom view.',
  requiredModules: [],
};

export const platformExtensionManagerEditPermission = {
  ...createPermission({
    name: 'platform.extension-manager.edit',
    attributes: {},
  }),
  description:
    'Can use the extension manager REST API to perform edit operations. Can use the admin page for the extension manager.',
  requiredModules: [],
};

export const platformWebhookEditPermission = {
  ...createPermission({
    name: 'platform.extensions.webhooks.edit',
    attributes: {},
  }),
  description:
    'Allows to view, create, edit, and delete webhooks in the platform.',
  requiredModules: [],
};

export const platformUsersAuthInvalidateTokensPermission = {
  ...createPermission({
    name: 'platform.users.auth.invalidate-tokens',
    attributes: {},
  }),
  description: 'Can invalidate the access tokens issued for a specific user.',
  requiredModules: [],
};

export const platformEnvironmentReadPermission = (env: string) => {
  return {
    ...createPermission({
      name: `platform.environment.read.${env}`,
      attributes: {},
    }),
    description: `Authorizes access to the "${env}" environment.`,
    requiredModules: [],
  };
};

export const practiceShaperEditPermission = {
  ...createPermission({
    name: 'practice-shaper.edit',
    attributes: {},
  }),
  description:
    'Gives access to the Practice Shaper settings in the Platform Settings page (see platform.settings.edit permissions below).',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const practiceShaperImportPermission = {
  ...createPermission({
    name: 'practice-shaper.import',
    attributes: {},
  }),
  description:
    'Gives access to the Practice Shaper import. The "Register new entity" button will show.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const rbacRoleCreatePermission = {
  ...createPermission({
    name: 'rbac.role.create',
    attributes: {},
  }),
  description: 'Create new roles without roles permissions or roles subjects.',
  requiredModules: [],
};

export const rbacRoleDeletePermission = {
  ...createPermission({
    name: 'rbac.role.delete',
    attributes: {},
  }),
  description: 'Delete user roles.',
  requiredModules: [],
};

export const rbacRoleEditPermission = {
  ...createPermission({
    name: 'rbac.role.edit',
    attributes: {},
  }),
  description:
    'Update existing roles and assigning roles permissions or roles subjects. Disabling of Authenticated Guest Users.',
  requiredModules: [],
};

export const platformUsersServiceAccountEditPermission = {
  ...createPermission({
    name: 'platform.users.service-accounts.edit',
    attributes: {},
  }),
  description: 'Register and unregister Service Accounts.',
  requiredModules: [],
};

export const platformUsersServiceAccountAccessTokenEditPermission = {
  ...createPermission({
    name: 'platform.users.service-accounts.access-tokens.edit',
    attributes: {},
  }),
  description: 'Manage access tokens for Service Accounts.',
  requiredModules: [],
};

export const platformUsersServiceAccountGitTokensEditPermission = {
  ...createPermission({
    name: 'platform.users.service-accounts.git-tokens.edit',
    attributes: {},
  }),
  description: 'Manage service account configurations for Service Accounts.',
  requiredModules: [],
};

export const catalogEntityReadPermission = {
  ...createPermission({
    name: 'catalog.entity.read',
    attributes: {},
  }),
  description: 'Authorizes reading one or more entities on the platform.',
  requiredModules: [],
};

export const catalogEntityCreatePermission = {
  ...createPermission({
    name: 'catalog.entity.create',
    attributes: {},
  }),
  description:
    'Authorizes creating new entities (like system or components). This includes registering existing entities from the related button.',
  requiredModules: [],
};

export const catalogEntityDeletePermission = {
  ...createPermission({
    name: 'catalog.entity.delete',
    attributes: {},
  }),
  description:
    'Authorizes removing entities from the platform. This enables the "Unregister Entity" button.',
  requiredModules: [],
};

export const catalogEntityRefreshPermission = {
  ...createPermission({
    name: 'catalog.entity.refresh',
    attributes: {},
  }),
  description:
    'Authorizes refreshing entities. This enables the refresh button on the detail pages.',
  requiredModules: [],
};

export const catalogLocationCreatePermission = {
  ...createPermission({
    name: 'catalog.location.create',
    attributes: {},
  }),
  description:
    'Authorizes the registration of one or more locations on the platform. This is needed when registering an existing entity into the platform  or scaffolding a new one.',
  requiredModules: [],
};

export const catalogLocationReadPermission = {
  ...createPermission({
    name: 'catalog.location.read',
    attributes: {},
  }),
  description: 'Authorizes reading one or more location from the platform.',
  requiredModules: [],
};

export const catalogLocationDeletePermission = {
  ...createPermission({
    name: 'catalog.location.delete',
    attributes: {},
  }),
  description: 'Authorizes deleting locations from the platform.',
  requiredModules: [],
};

export const systemPrototypeEditPermission = {
  ...createPermission({
    name: 'builder.system-prototype.edit',
    attributes: {},
  }),
  description: 'Can use the Backlog section',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesManagePermission = {
  ...createPermission({
    name: 'control-plane.project.team-roles.manage',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Manages a specific project, including other team roles delegation',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesManageLimitedPermission = {
  ...createPermission({
    name: 'control-plane.project.team-roles.limited-manage',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description: 'Manages a specific project as a limited assignee',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesDataAccessManagePermission = {
  ...createPermission({
    name: 'control-plane.project.manage-access',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Manages access control requests for the consumables of a specific project.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesDataAccessManageLimitedPermission = {
  ...createPermission({
    name: 'control-plane.project.limited-manage-access',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Manages access control requests for the consumables of a specific project as a limited assignee.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

// FIXME: for shape 6 this is not listed as available platform permission, since we don't want the developer team role
export const teamRolesMaintainPermission = {
  ...createPermission({
    name: 'control-plane.project.maintain',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Maintains a specific project, allowing them to receive notifications about the project and its components.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

// FIXME: for shape 6 this is not listed as available platform permission, since we don't want the developer team role
export const teamRolesMaintainLimitedPermission = {
  ...createPermission({
    name: 'control-plane.project.limited-maintain',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Maintains a specific project as a limited assignee, allowing them to receive notifications about the project and its components.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesTroubleshootPermission = {
  ...createPermission({
    name: 'control-plane.project.team-roles.troubleshoot',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Authorizes all actions on team roles assignments. This allows administrator users to troubleshoot a project team role delegation.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesManageLifecyclePermission = {
  ...createPermission({
    name: 'control-plane.project.manage-lifecycle',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Manages the lifecycle of a specific project, including approving or rejecting deploy requests.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const teamRolesManageLifecycleLimitedPermission = {
  ...createPermission({
    name: 'control-plane.project.limited-manage-lifecycle',
    attributes: {},
    resourceType: RESOURCE_TYPE_WITBOOST_ENTITY,
  }),
  description:
    'Manages the lifecycle of a specific project as a limited assignee.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const controlPlaneFeatureRequestCreatePermission = {
  ...createPermission({
    name: 'control-plane.feature-request.create',
    attributes: {},
  }),
  description:
    'Authorizes the submission of a new Feature Request against a System or Component.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const workflowEngineDefinitionViewPermission = {
  ...createPermission({
    name: 'workflow-engine.definition.view',
    attributes: {},
  }),
  description: 'Authorizes viewing workflow definitions.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const workflowEngineInstanceViewPermission = {
  ...createPermission({
    name: 'workflow-engine.instance.view',
    attributes: {},
  }),
  description: 'Authorizes viewing workflow instances and their details.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const workflowEngineTransitionCreatePermission = {
  ...createPermission({
    name: 'workflow-engine.transition.create',
    attributes: {},
  }),
  description:
    'Authorizes requesting state transitions on workflow instances (e.g. approving or rejecting deploy requests).',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const workflowEngineDefinitionManagePermission = {
  ...createPermission({
    name: 'workflow-engine.definition.manage',
    attributes: {},
  }),
  description: 'Authorizes activating or deactivating workflow definitions.',
  requiredModules: [WitboostCommercialModule.ControlPlane],
};

export const catalogPermissions: () => RbacPermission[] = () => [
  catalogEntityReadPermission,
  catalogEntityCreatePermission,
  catalogEntityDeletePermission,
  catalogEntityRefreshPermission,
  catalogLocationCreatePermission,
  catalogLocationReadPermission,
  catalogLocationDeletePermission,
];

export const cgpPermissions: () => RbacPermission[] = () => [
  cgpEntityView,
  cgpEntityEdit,
];

export const platformEntitiesPermissions: () => RbacPermission[] = () => [
  catalogPlatformCreatePermission,
  catalogPlatformDeletePermission,
  catalogPlatformRefreshPermission,
];

export const platformSettingsPermissions: () => RbacPermission[] = () => [
  platformSettingsEditPermission,
  platformCustomViewEditPermission,
  platformExtensionManagerEditPermission,
  systemPrototypeEditPermission,
  platformWebhookEditPermission,
  platformUsersAuthInvalidateTokensPermission,
  platformUsersServiceAccountEditPermission,
  platformUsersServiceAccountAccessTokenEditPermission,
  platformUsersServiceAccountGitTokensEditPermission,
];

export const practiceShaperPermissions: () => RbacPermission[] = () => [
  practiceShaperEditPermission,
  practiceShaperImportPermission,
];

export const rbacPermissions: () => RbacPermission[] = () => [
  rbacRoleCreatePermission,
  rbacRoleEditPermission,
  rbacRoleDeletePermission,
];

export const teamRolesPermissions: () => RbacPermission[] = () => [
  teamRolesManagePermission,
  teamRolesManageLimitedPermission,
  teamRolesDataAccessManagePermission,
  teamRolesDataAccessManageLimitedPermission,
  teamRolesTroubleshootPermission,
  teamRolesManageLifecyclePermission,
  teamRolesManageLifecycleLimitedPermission,
];

export const builderPermissions: (
  environments: Environment[],
) => RbacPermission[] = environments => [
  builderDpSnapshotCreatePermission,
  builderDpReleasePermission,
  builderDpCommitPermission,
  ...environments.map(env => builderDpDeployPermission(env.name)),
  builderDpPoliciesTest,
  builderDpNewVersion,
  builderSoftwareCatalogView,
  controlPlaneFeatureRequestCreatePermission,
];

export const platformEnvironmentsPermissions: (
  environments: Environment[],
) => RbacPermission[] = environments => [
  ...environments.map(env => platformEnvironmentReadPermission(env.name)),
];

export const workflowEnginePermissions: () => RbacPermission[] = () => [
  workflowEngineDefinitionViewPermission,
  workflowEngineDefinitionManagePermission,
  workflowEngineInstanceViewPermission,
  workflowEngineTransitionCreatePermission,
];
